import { TRACK_EVENT_NAMES, TRACK_EVENT_TYPES } from 'shared/consts/';
import { replace } from 'store/router/actions';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { track } from '@hqo/web-tracking';
import { useBooleanState } from '@hqo/react-components-library/dist/hooks';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'hooks/use-search-params.hook';

interface UseFilterBarReturnValues {
  reverseAnimation: boolean;
  onOpenModal: VoidFunction;
  onCloseModal: VoidFunction;
  onCTAClick: VoidFunction;
  pathname: string;
}

export const useFilterModalNavigation = (): UseFilterBarReturnValues => {
  const dispatch = useDispatch();
  const { search, pathname } = useLocation();
  const { value: reverseAnimation, toggle: toggleReverseAnimation } = useBooleanState(true);
  const { ...queryParams } = useSearchParams();
  const { filterByType, startDate, startDates, startTime, endTime, duration, filterByFloor } = queryParams;
  const multiDay = !!startDates;

  const pathnameForReplaceAction = useMemo(() => (pathname === '/filter-by' ? 'filter-by' : '/filter-by'), [pathname]);

  const filterEntries = Object.entries({
    filterByType,
    startDate,
    multiDay,
    startTime,
    endTime,
    duration,
    filterByFloor,
  });
  const filtersForTracking = filterEntries.map(entry => (entry[1] ? entry[0] : null)).filter(value => value !== null);

  const onOpenModal = useCallback(() => {
    const formattedPathName = pathname[pathname.length - 1] === '/' ? pathname : `${pathname}/`;
    toggleReverseAnimation();
    dispatch(replace(`${formattedPathName}filter-by${search}`));
  }, [toggleReverseAnimation, dispatch, search, pathname]);

  const onCTAClick = useCallback(() => {
    track(
      TRACK_EVENT_NAMES.FILTER_SHOW_RESULTS_CLICK,
      {
        type: TRACK_EVENT_TYPES.ACTION,
        filters_applied: filtersForTracking,
      },
      { sendToHqoTracking: true },
    );
    toggleReverseAnimation();
    dispatch(replace(`${pathname.replace(pathnameForReplaceAction, '')}${search}`));
  }, [filtersForTracking, toggleReverseAnimation, dispatch, pathname, pathnameForReplaceAction, search]);

  const onCloseModal = useCallback(() => {
    toggleReverseAnimation();
    dispatch(replace(`${pathname.replace(pathnameForReplaceAction, '')}${search}`));
  }, [dispatch, toggleReverseAnimation, pathname, pathnameForReplaceAction, search]);

  return {
    reverseAnimation,
    onOpenModal,
    onCloseModal,
    onCTAClick,
    pathname,
  };
};
